import { useEffect, useState } from "react";
import { useFillHeight } from "../../grid/useFillHeight";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";

import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import {
  ClinicalFormInfo,
  GridDataResponse,
  PatientGridAssessment,
} from "clinical-forms-experience-client";

import { formatDate, formatDateTime } from "../../helper/dateFormatter";
import { setPageTitle } from "../../store/slices/pageTitleSlice";

import AssessmentHeader, { AssessmentFilters } from "./Header.assessment";

import "../../grid/GenericGrid";
import { AssessmentsGridActionIcon } from "./GridActionIcon.assessment";

export const AssessmentGrid = () => {
  // State variables
  const { ref: gridContainerRef, height: gridHeight } = useFillHeight<HTMLDivElement>();
  const [gridReloadKey, setGridReloadKey] = useState(0);
  const [filters, setFilters] = useState<AssessmentFilters>({
    searchText: "",
    completedBy: [],
    assessmentType: [],
    assessmentStatus: [],
  });
  const [data, setData] = useState<PatientGridAssessment[]>([]);

  // Hooks
  const clinicalformsApi = createClinicalFormsApi();
  const { patientId } = useParams<{ patientId: string }>();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const assessmentId = searchParams.get("assessmentId");

  // Load data for the grid
  const loadGridData = async (): Promise<GridDataResponse> => {
    const result =
      await clinicalformsApi.clinicalFormsControllerGetAssessmentsGridData(
        patientId!
      );
    const gridData = result.data;
    setData(gridData);
    return {
      data: gridData,
      total: gridData.length,
    };
  };

  // Reload grid
  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  // Filter data based on filters
  const getFilteredData = () => {
    return data.filter((item) => {
      let matches = true;

      // Search Text filtering
      if (filters.searchText) {
        if (
          !item.assessmentType ||
          !item.assessmentType
            .toLowerCase()
            .includes(filters.searchText.toLowerCase())
        ) {
          matches = false;
        }
      }

      // Completed By filtering
      if (filters.completedBy.length) {
        if (
          !item.lastCompletedBy ||
          !filters.completedBy.includes(item.lastCompletedBy)
        ) {
          matches = false;
        }
      }

      // Assessment Type filtering
      if (filters.assessmentType.length) {
        if (
          !item.assessmentTypeId ||
          !filters.assessmentType.includes(item.assessmentTypeId)
        ) {
          matches = false;
        }
      }

      // Assessment Status filtering
      if (filters.assessmentStatus.length) {
        if (
          !item.lastStatus ||
          !filters.assessmentStatus.includes(item.lastStatus)
        ) {
          matches = false;
        }
      }

      return matches;
    });
  };

  // Filtered data for rendering
  const filteredData = getFilteredData();

  // Column definitions for the grid
  const colDefs: ColDef[] = [
    {
      field: "assessmentType",
      minWidth: 400,
      maxWidth: 900,
      headerName: "Assessment Type",
      pinned: "left",
      sortable: true,
    },
    {
      field: "lastScore",
      minWidth: 150,
      maxWidth: 180,
      headerName: "Last Score",
      pinned: "left",
      sortable: true,
      valueFormatter: (params) => params.data.lastScore ?? "-",
    },
    {
      field: "lastSubmittedAt",
      minWidth: 260,
      maxWidth: 300,
      headerName: "Last Submitted On",
      valueFormatter: (params) => formatDateTime(params.data.lastSubmittedAt),
      sortable: true,
    },
    {
      field: "lastCompletedBy",
      minWidth: 260,
      maxWidth: 300,
      headerName: "Last Completed By",
      sortable: true,
      valueFormatter: (params) => params.data.lastCompletedBy?.trim() || "-",
    },
    {
      field: "nextDueDate",
      minWidth: 260,
      maxWidth: 300,
      headerName: "Next Due",
      valueFormatter: (params) => formatDate(params.data.nextDueDate),
      sortable: true,
    },
    {
      field: "lastStatus",
      minWidth: 260,
      maxWidth: 300,
      headerName: "Status",
      sortable: true,
      valueFormatter: (params) => params.data.lastStatus ?? "-",
    },
    {
      field: "",
      minWidth: 50,
      maxWidth: 60,
      cellRenderer: AssessmentsGridActionIcon,
      pinned: "right",
      sortable: false,
    },
  ];

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    editable: false,
    suppressMenu: true,
  };

  // Effects
  useEffect(() => {
    dispatch(setPageTitle("Assessments: Patient Profile | Spire360"));
  }, []);

  useEffect(() => {
    loadGridData();
  }, [gridReloadKey]);

  return (
    <>
      <AssessmentHeader onUpdateFilters={setFilters}/>
      <div className="d-flex px-3 px-md-4 px-lg-5 d-flex align-items-stretch">
        <div
          ref={gridContainerRef}
          className={"generic-grid ag-theme-quartz flex-fill py-5"}
          style={{ width: "100%", height: gridHeight ?? "" }}
        >
          <AgGridReact
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            rowModelType="clientSide"
            pagination={true}
            rowData={filteredData}
            cacheBlockSize={20}
            suppressDragLeaveHidesColumns={true}
            suppressColumnMoveAnimation={true}
            suppressMovableColumns={true}
            suppressNoRowsOverlay={false}
            suppressContextMenu={true}
          />
        </div>
      </div>
    </>
  );
};
